import { ChevronRight, X, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { useGuidanceVisibility } from "@/components/layout/use-guidance-visibility";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type GuidanceTone = "action" | "waiting" | "blocked";

/**
 * One step in a guided setup flow. Same single-current-step semantics
 * as `SetupChecklist` (the card surfaces the first incomplete step and
 * hides itself when every step is complete) — the visual surface is
 * the difference: tinted icon disc + step counter + footer CTA, no
 * left-stripe attention chrome.
 */
export interface GuidanceStep {
  id: string;
  /** Per-step lucide icon — the disc on the left gives each step a
   *  scannable visual identity (Users for contact, ReceiptText for
   *  bill-related, Mail for RFQ, etc.). */
  icon: LucideIcon;
  title: string;
  description: string;
  /** Lightweight predicate; evaluates on every render. */
  isComplete: boolean;
  /** Tone tints the icon disc + the CTA button.
   *    action  → teal-dim disc, default primary button.
   *    waiting → amber-dim disc, outline button (passive peek).
   *    blocked → red-dim disc, red-bordered outline button. */
  tone?: GuidanceTone;
  /** Custom action node — use when the step needs multi-button rows
   *  (Customer Accepted / Rejected) or a non-navigation mutation
   *  (Mark Proposed). When present, `cta` is ignored. */
  action?: ReactNode;
  /** Single-action footer button. Renders as a tone-styled `<Button>`
   *  with a trailing arrow when no `action` is supplied. */
  cta?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Lower-priority prompt rendered in a footer "Also" row.
 *
 * `kind` controls the trailing affordance:
 *   · `navigate` (default) — renders a trailing chevron, signaling the
 *     click takes the user somewhere.
 *   · `slideout` — no chevron, since the click opens an overlay rather
 *     than navigating; the chevron would mis-signal the action.
 */
export interface GuidanceWorkAheadChip {
  id: string;
  label: string;
  onClick: () => void;
  kind?: "navigate" | "slideout";
}

interface GuidanceCardProps {
  steps: ReadonlyArray<GuidanceStep>;
  /** Optional secondary prompts rendered in the footer "Also" row.
   *  Empty / omitted → no row. */
  workAhead?: ReadonlyArray<GuidanceWorkAheadChip>;
  /** Override the "Step N of M" counter. Use when the index/total are
   *  computed elsewhere (e.g. server-side) and only the current step is
   *  passed in `steps`, rather than the full chain. */
  stepOverride?: { index: number; total: number };
  className?: string;
}

const TONE_DISC: Record<GuidanceTone, string> = {
  action: "bg-primary-dim text-primary",
  waiting: "bg-warning-dim text-warning",
  blocked: "bg-destructive-dim text-destructive",
};

/**
 * Substantial card surface for the entity's current setup step.
 *
 *   ┌─────────────────────────────────────────────────────[✕]─┐
 *   │ ┌────┐  Title text                Step 3 of 7            │
 *   │ │icon│  Description wraps here…                          │
 *   │ └────┘                                                   │
 *   │        [Primary action]  [Secondary]                     │
 *   │ ─────────────────────────────────────────────────────── │
 *   │        Also: chip · chip · chip                          │
 *   └────────────────────────────────────────────────────────┘
 *
 * Picks the first incomplete step from `steps` and surfaces it. Self-
 * hides when every step is complete, when `steps` is empty, or when
 * the user has hidden guidance globally via the topbar toggle.
 *
 * Distinct from `SetupChecklist` (which carries warning chrome — left
 * stripe, dim text, inline `→` link) because guidance shouldn't read
 * as an alarm. It's a destination: "here's what to do next."
 */
export function GuidanceCard({
  steps,
  workAhead,
  stepOverride,
  className,
}: GuidanceCardProps) {
  const [hidden, setHidden] = useGuidanceVisibility();
  if (hidden) return null;
  if (steps.length === 0) return null;

  const currentIdx = steps.findIndex((s) => !s.isComplete);
  if (currentIdx < 0) return null;

  const current = steps[currentIdx];
  const tone = current.tone ?? "action";
  const Icon = current.icon;
  const chips = workAhead ?? [];

  return (
    <div
      className={cn(
        "relative flex flex-col gap-4 rounded-md border border-border bg-card p-5",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setHidden(true)}
        aria-label="Hide setup guidance"
        title="Hide setup guidance"
        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-row-hover hover:text-foreground"
      >
        <X size={14} />
      </button>

      <div className="flex items-start gap-3 pr-7">
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
            TONE_DISC[tone],
          )}
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <h3 className="text-sm font-semibold leading-tight text-foreground">
              {current.title}
            </h3>
            <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-text-dim">
              Step {stepOverride ? stepOverride.index : currentIdx + 1} of{" "}
              {stepOverride ? stepOverride.total : steps.length}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {current.description}
          </p>
        </div>
      </div>

      {(current.action || current.cta) && (
        <div className="ml-12 flex flex-wrap items-center gap-2">
          {current.action ?? <CtaButton tone={tone} cta={current.cta!} />}
        </div>
      )}

      {chips.length > 0 && (
        <div className="ml-12 flex flex-wrap items-center gap-2 border-t border-border/40 pt-3">
          <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-text-dim">
            Also
          </span>
          {chips.map((chip) => (
            <Button
              key={chip.id}
              type="button"
              variant="subtle"
              size="sm"
              onClick={chip.onClick}
            >
              {chip.label}
              {(chip.kind ?? "navigate") === "navigate" && (
                <ChevronRight size={12} />
              )}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function CtaButton({
  tone,
  cta,
}: {
  tone: GuidanceTone;
  cta: { label: string; onClick: () => void };
}) {
  // The `cta` prop is for navigation steps (slideout-driven steps use
  // `action` and supply their own button); the trailing chevron signals
  // "this takes you somewhere else."
  if (tone === "blocked") {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={cta.onClick}
        className="border-destructive-border text-destructive hover:bg-destructive hover:text-white"
      >
        {cta.label}
        <ChevronRight size={12} />
      </Button>
    );
  }
  if (tone === "waiting") {
    return (
      <Button type="button" variant="outline" size="sm" onClick={cta.onClick}>
        {cta.label}
        <ChevronRight size={12} />
      </Button>
    );
  }
  return (
    <Button type="button" size="sm" onClick={cta.onClick}>
      {cta.label}
      <ChevronRight size={12} />
    </Button>
  );
}
